import { getCsrfValue } from "~/common/utils/ApiHelper";
import type { PriceInfo } from "~/domains/pricing/api/PriceInfo";
import type { LinearConfigurationDTO } from "~/features/configurator/Linear/dtos";
import type { LinearConfiguratorState } from "~/features/configurator/Linear/redux/types";
import { resolveApiUrl } from "~/utils/url/urlResolver";
import type { ConfiguratorInitResult } from "./configuratorInitApi";
import type { LinearConfiguration } from "~/features/configurator/Linear/models";
import { selectPrice } from "~/utils/pricePicker";
import { ensureActiveCartForContext } from "~/domains/cart/activeCartPort";
import {
  buildConfiguratorAddToCartCommand,
  buildConfiguratorResetCommand,
  buildConfiguratorResetStepCommand,
  buildConfiguratorResetStepCommandForTargets,
  type ConfiguratorAddToCartCommand,
  type ConfiguratorResetCommand,
  type ConfiguratorResetStepCommand,
  type ConfiguratorUpdateCommand,
  type ConfiguratorCsticTarget,
} from "./configuratorCommands";

type ConfiguratorCommand =
  | ConfiguratorUpdateCommand
  | ConfiguratorResetStepCommand
  | ConfiguratorResetCommand
  | ConfiguratorAddToCartCommand;

export interface ConfiguratorFullResetResult {
  configuration: LinearConfiguration;
  helpUrl: string;
}

export async function postConfiguratorCommand<T>(
  url: string,
  command: ConfiguratorCommand,
  operation: string,
  parseResponse: (response: Response) => Promise<T> = (response) => response.json() as Promise<T>,
): Promise<T> {
  const response = await fetch(resolveApiUrl(url), {
    body: JSON.stringify(command),
    cache: "no-cache",
    credentials: "same-origin",
    method: "POST",
    mode: "cors",
    headers: {
      dataType: "json",
      "content-type": "application/json",
      CSRFToken: getCsrfValue(),
    },
    redirect: "follow",
  });

  if (!response.ok) {
    console.error(`Error ${operation}: ${response.status}`);
    throw new Error(String(response.status));
  }

  return parseResponse(response);
}

export function sendConfiguratorResetStep(
  state: LinearConfiguratorState,
  resetTargets?: ConfiguratorCsticTarget[],
): Promise<LinearConfigurationDTO> {
  const command = resetTargets
    ? buildConfiguratorResetStepCommandForTargets(state, resetTargets)
    : buildConfiguratorResetStepCommand(state);

  return postConfiguratorCommand("/cpq/reset-step", command, "resetting configurator step");
}

export function sendConfiguratorFullReset(state: LinearConfiguratorState): Promise<ConfiguratorInitResult> {
  return postConfiguratorCommand("/cpq/reset", buildConfiguratorResetCommand(state), "resetting configurator");
}

export async function sendConfiguratorAddToCart(
  state: LinearConfiguratorState,
  priceInfo: PriceInfo | null,
): Promise<string> {
  const command = buildConfiguratorAddToCartCommand(
    state,
    priceInfo?.priceReference || state.priceReference,
    selectPrice(state.quantity, priceInfo)?.value,
  );
  const { resourceId: activeCartId } = await ensureActiveCartForContext();
  const currentUrl = window.location.href.substring(0, window.location.href.lastIndexOf("/"));

  return postConfiguratorCommand(
    `${currentUrl}/addToCart2?activeCartId=${encodeURIComponent(activeCartId)}`,
    command,
    "adding configurator product to cart",
    async (response) => {
      const continuationConfigId = await response.json();
      if (!continuationConfigId) {
        throw new Error("Add-to-cart did not return a continuation configuration");
      }
      return continuationConfigId;
    },
  );
}
